import React from "react";
import { getByPath, setSafePath } from "../../../util/object-util";
import FieldMarkersEditor from "../components/field-selectors/FieldMarkersEditor";

// Flag-football extension for the generic Configuration form. Everything the
// settings screens know about flag football lives here — presets, the field
// marker editors and the coupled-value bookkeeping — so Configuration itself
// stays sport-agnostic. Parents pass this object as the `extension` prop when
// the sport is flag football.

const RED_ZONE_TYPE = "redZone";
// Changing the field length invalidates yard-based marker config: spots
// beyond the new length would clamp onto the ruler edge and fail
// validation on save. Sanitize the coupled values in the same update —
// drop out-of-range first-down spots, clamp/drop field areas, clamp the
// red zone length.
const onFieldLengthChange = (newLength, formApi) => {
	// While the input is being cleared/typed the value can be NaN or 0;
	// just store it and skip marker recompute (Array(NaN) would throw).
	if (!Number.isFinite(newLength) || newLength <= 0) {
		formApi.setFieldValue("general.fieldLength", newLength);
	} else {
		let next = setSafePath(formApi.values, ["general", "fieldLength"], newLength);

		// Changing the field length invalidates hand-authored spots (a
		// midfield-25 set for a 50yd field is meaningless on 40yd), so
		// always recompute the uniform zone-to-gain set for the new
		// length — matching core's game-start formula (multiples of
		// fieldLength / (teamZoneQty * 2), fractional boundaries dropped)
		// — and clear the custom flag. Presets set length and spots
		// together in one update, so they are unaffected.
		const spots = getByPath(formApi.values, "general.firstDownYardSet");
		if (Array.isArray(spots)) {
			const teamZoneQty = getByPath(formApi.values, "general.teamZoneQty") || 2;
			const zoneSize = newLength / (teamZoneQty * 2);
			const uniform = Array.from(Array(newLength + 1).keys()).filter(
				(k) => k !== 0 && k % zoneSize === 0
			);
			next = setSafePath(next, ["general", "firstDownYardSet"], uniform);
			next = setSafePath(next, ["general", "customFirstDownYardSet"], false);
		}

		// Reposition areas for the new length. An area anchored to the
		// far goal line (end === old length, e.g. a preset's far red
		// zone) slides to keep touching the new goal line at the same
		// width; others keep their start and clamp their end. Areas that
		// can't fit at all are dropped.
		const oldLength = getByPath(formApi.values, "general.fieldLength");
		const areas = getByPath(formApi.values, "general.fieldAreaSet");
		if (Array.isArray(areas)) {
			const repositioned = areas
				.map((a) => {
					let mapped = a;
					if (a.end === oldLength) {
						const width = a.end - a.start;
						mapped = {
							...a,
							start: Math.max(0, newLength - width),
							end: newLength
						};
					} else {
						mapped = { ...a, end: Math.min(a.end, newLength) };
					}
					return mapped;
				})
				.filter((a) => a.start < a.end);
			next = setSafePath(next, ["general", "fieldAreaSet"], repositioned);
		}

		const redZoneLength = getByPath(formApi.values, "general.redZoneLength");
		if (typeof redZoneLength === "number" && redZoneLength > newLength) {
			next = setSafePath(next, ["general", "redZoneLength"], newLength);
		}

		formApi.setValues(next);
	}
};

// Persist marker edits, then keep the coupled scalar config in sync:
// editing the first-down spots flags the set as crew-authored (so core
// stops recomputing uniform zones), and a goal-line red-zone area drives
// redZoneLength (the only area type that feeds stats).
const onMarkersChange = (element, next, formApi) => {
	formApi.setFieldValue(element.key, next);
	if (element.key.endsWith("firstDownYardSet")) {
		formApi.setFieldValue("general.customFirstDownYardSet", true);
	}
	if (element.key.endsWith("fieldAreaSet")) {
		const redZone = next.find((a) => a.type === RED_ZONE_TYPE && a.start === 0);
		if (redZone) {
			formApi.setFieldValue("general.redZoneLength", redZone.end);
		}
	}
};

const flagFootballConfiguration = {
	componentRenderers: {
		fieldMarkers: (element, formApi) => (
			<FieldMarkersEditor
				key={element.key}
				title={element.title}
				value={getByPath(formApi.values, element.key) || []}
				fieldLength={getByPath(formApi.values, "general.fieldLength")}
				markerKind={element.markerKind}
				areaTypes={element.areaTypes || []}
				disabled={formApi.disabled || element.disabled || formApi.preview}
				onChange={(next) => onMarkersChange(element, next, formApi)}
			/>
		)
	},

	// Field length changes must sanitize the coupled marker config, so claim
	// that one path instead of letting formik's plain handleChange store it.
	interceptChange: (path, value, formApi) => {
		let handled = false;
		if (path === "general.fieldLength") {
			onFieldLengthChange(Number(value), formApi);
			handled = true;
		}
		return handled;
	},

	// Marker rulers span the full settings row. Under Yard To Gain the spots
	// ruler is hidden (form-data visibleWhen) and core's First Down Every
	// (yds) field takes the cell as a plain number input.
	isFullWidth: (element) => element.component === "fieldMarkers"
};

export default flagFootballConfiguration;
